"use client";

// Adaptiv polling: när det inte är någon aktiv lektion (99 % av tiden för
// flesta elever) räcker det att kolla varje minut. Under aktiv lektion 15 s
// (så att "vill du vara med i spelet?"-popupen dyker upp snabbt efter att
// läraren startat ett spel). Så länge ett spel pågår och eleven inte
// deltar ännu pollar vi extra tätt (5 s) så ikonen/popupen syns direkt.
// (Räck-upp-hand och dess 6 s-takt togs bort 2026-10-21, Eriks beslut.)
// Intervallen (AKTIV_LEKTION_*) bor i src/lib/klient-intervall.ts.
import { AKTIV_LEKTION_IDLE_MS, AKTIV_LEKTION_NAV_PAUS_MS, LIVE_PAUS_MIN_MS } from "@/lib/klient-intervall";
import { aktivLektionIntervall } from "@/lib/elev-pollintervall";
import { AKTIV_LEKTION_NU } from "@/lib/aktiv-lektion-nu";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePathname, useRouter } from "next/navigation";
import { skapaLiveKlient } from "@/lib/live-klient";
import { RepListaModal, type RepListaItem } from "@/components/student/RepListaModal";
import Modal from "@/components/ui/Modal";
import { SPEL_NAMN, type AktivtSpel } from "@/lib/spel/types";
import AktivitetLampa from "@/components/lektion/AktivitetLampa";

interface ExtraMaterial {
  id: string;
  titel: string;
  url: string;
  pdfUrl?: string;
  pdfFilnamn?: string;
}

interface AktivLektion {
  klassId: string;
  klassNamn: string;
  start: string;
  slut: string;
  raster: { id: string; start: string; langd: number }[];
  extraMaterial?: ExtraMaterial[];
  titel: string;
  repetitioner?: RepListaItem[];
  spel: AktivtSpel | null;
  // Klassens pågående interaktiva genomgång (null nästan alltid). Äldre
  // svar utan fältet behandlas som "ingen genomgång".
  genomgang?: AktivGenomgang | null;
  // Klassens öppna lektionsreflektion (null nästan alltid).
  reflektion?: { id: string; besvarad: boolean } | null;
  // Klassens pågående begreppsjakt (null nästan alltid); med = eleven har gått med.
  begreppsjakt?: { id: string; med: boolean } | null;
  // Klassens pågående Matte-detektiv (null nästan alltid); med = eleven har gått med.
  mattedetektiv?: { id: string; med: boolean } | null;
  // Klassens pågående problemlösning (null nästan alltid); med = eleven har gått med. slag "oppna" = Öppna frågor
  // på samma motor (Dokument/oppna-fragor-spec.md).
  problemlosning?: { id: string; med: boolean; slag?: "problemlosning" | "oppna" } | null;
}

interface AktivGenomgang {
  id: string;
  fas: "bildspel" | "ovning" | "losning";
  // Delen vars fråga är öppen (fas ovning/losning), annars null.
  oppenDel: number | null;
}

function timeToMinutes(t: string): number | null {
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

function minutesToHHMM(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

// "Inte nu" på gå-med-popupen kommer igen först nästa gång läraren startar ett
// NYTT spel (nytt id) — sessionStorage räcker (per flik, rensas mellan lektioner
// naturligt eftersom ett gammalt spel-id aldrig återanvänds). Best-effort: en
// privat flik eller blockerad site-data ska inte krascha komponenten.
function harTackatNejTillSpel(spelId: string): boolean {
  try {
    return sessionStorage.getItem(`spel-nekad-${spelId}`) === "1";
  } catch {
    return false;
  }
}
function markeraNejTillSpel(spelId: string): void {
  try {
    sessionStorage.setItem(`spel-nekad-${spelId}`, "1");
  } catch {
    // Ignoreras — popupen kan dyka upp igen, inte hela världen.
  }
}

// Samma sak för genomgången (genomgang-spec §4d), men nyckeln är id + fas +
// öppen del: "Inte nu" på bildspelet tystar resten av bildspelet, medan en NY
// öppnad fråga (ny del = ny nyckel) får en ny popup.
function genomgangNyckel(g: AktivGenomgang): string {
  return `${g.id}:${g.fas}:${g.oppenDel}`;
}
function harTackatNejTillGenomgang(nyckel: string): boolean {
  try {
    return sessionStorage.getItem(`genomgang-nekad-${nyckel}`) === "1";
  } catch {
    return false;
  }
}
function markeraNejTillGenomgang(nyckel: string): void {
  try {
    sessionStorage.setItem(`genomgang-nekad-${nyckel}`, "1");
  } catch {
    // Ignoreras, som för spelet.
  }
}

// Lektionsreflektionen: "Inte nu" tystar popupen för den reflektionen i fliken.
function harTackatNejTillReflektion(id: string): boolean {
  try {
    return sessionStorage.getItem(`reflektion-nekad-${id}`) === "1";
  } catch {
    return false;
  }
}
function markeraNejTillReflektion(id: string): void {
  try {
    sessionStorage.setItem(`reflektion-nekad-${id}`, "1");
  } catch {
    // Ignoreras, som för spelet.
  }
}

// Begreppsjakten: "Inte nu" tystar inbjudan för den jakten i fliken.
function harTackatNejTillJakt(id: string): boolean {
  try {
    return sessionStorage.getItem(`begreppsjakt-nekad-${id}`) === "1";
  } catch {
    return false;
  }
}
function markeraNejTillJakt(id: string): void {
  try {
    sessionStorage.setItem(`begreppsjakt-nekad-${id}`, "1");
  } catch {
    // Ignoreras, som för spelet.
  }
}

// Matte-detektiven: "Inte nu" tystar inbjudan för den omgången i fliken.
function harTackatNejTillDetektiv(id: string): boolean {
  try {
    return sessionStorage.getItem(`mattedetektiv-nekad-${id}`) === "1";
  } catch {
    return false;
  }
}
function markeraNejTillDetektiv(id: string): void {
  try {
    sessionStorage.setItem(`mattedetektiv-nekad-${id}`, "1");
  } catch {
    // Ignoreras, som för spelet.
  }
}

// Problemlösningen: "Inte nu" tystar inbjudan för den aktiviteten i fliken.
function harTackatNejTillProblemlosning(id: string): boolean {
  try {
    return sessionStorage.getItem(`problemlosning-nekad-${id}`) === "1";
  } catch {
    return false;
  }
}
function markeraNejTillProblemlosning(id: string): void {
  try {
    sessionStorage.setItem(`problemlosning-nekad-${id}`, "1");
  } catch {
    // Ignoreras, som för spelet.
  }
}

// A vote (spel-spec §20) has no game yet — its spelTyp is only a placeholder.
function spelIkonText(spel: AktivtSpel): string {
  const namn = spel.rostning ? "spelet" : SPEL_NAMN[spel.spelTyp];
  if (spel.deltar) return `Gå till ${namn}`;
  return spel.rostning ? "Gå med i spelet och rösta" : `Gå med i ${namn}`;
}

function genomgangIkonText(g: AktivGenomgang): string {
  return g.fas === "ovning" ? "Svara på lärarens fråga i genomgången" : "Öppna genomgången";
}

/**
 * Visar en kompakt lektions-bar i elevens header — bara när någon av deras
 * klasser har en aktiv lektion just nu (lärare har fyllt i start + slut, och
 * vi är inom det fönstret). Pollar /api/student/aktiv-lektion var 30 sek.
 */
/** The done-flag's key: the triple + interval (a bare slug is not unique per course). */
const repNyckel = (r: { kapitelId: string; avsnittId: string; slug: string; intervall: string }) =>
  `${r.kapitelId}/${r.avsnittId}/${r.slug}::${r.intervall}`;

/** The pages of the activities the student has joined: there the bar polls like on the genomgång's own page. */
function egnaAktivitetssidor(aktiv: AktivLektion | null): string[] {
  if (!aktiv) return [];
  return [
    ...(aktiv.spel?.deltar ? [`/spel/${aktiv.spel.id}`] : []),
    ...(aktiv.begreppsjakt?.med ? [`/begreppsjakt/${aktiv.begreppsjakt.id}`] : []),
    ...(aktiv.mattedetektiv?.med ? [`/mattedetektiv/${aktiv.mattedetektiv.id}`] : []),
    ...(aktiv.problemlosning?.med ? [`/problemlosning/${aktiv.problemlosning.id}`] : []),
  ];
}

export default function LektionsBarForElev({ provPagar = false }: { provPagar?: boolean }) {
  const [lektion, setLektion] = useState<AktivLektion | null>(null);
  const [now, setNow] = useState(() => new Date());
  const [materialModalOpen, setMaterialModalOpen] = useState(false);
  const [repModalOpen, setRepModalOpen] = useState(false);
  // Spel-id eleven själv stängt "Vill du vara med?"-popupen för i den här
  // fliken — ett nytt spel har ett nytt id, så det dyker upp igen då.
  const [avvisatSpelId, setAvvisatSpelId] = useState<string | null>(null);
  // Samma för genomgången: nycklar (id:fas:del) eleven tryckt "Inte nu" på.
  const [avvisadeGenomgangar, setAvvisadeGenomgangar] = useState<string[]>([]);
  const [avvisadReflektion, setAvvisadReflektion] = useState<string | null>(null);
  const [avvisadJakt, setAvvisadJakt] = useState<string | null>(null);
  const [avvisadDetektiv, setAvvisadDetektiv] = useState<string | null>(null);
  const [avvisadProblemlosning, setAvvisadProblemlosning] = useState<string | null>(null);
  const pathname = usePathname();
  const router = useRouter();
  const routerRef = useRef(router);
  useEffect(() => {
    routerRef.current = router;
  }, [router]);
  // Pollingen läser sidan via ref (effekten körs bara en gång): på genomgångens
  // egen sida har sidan egen poll och baren går i vanlig lektionstakt.
  const pathnameRef = useRef(pathname);
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);
  // Pause-flagga som pathname-effekten flippar — mainloopen läser den och
  // skjuter pågående fetch + nästa tick framåt så nav-rendering får företräde.
  const rescheduleRef = useRef<(() => void) | null>(null);
  // A started exam (ProvVakt's answer, passed by Nav): the proxy refuses this route during it (423) and the
  // route would answer null anyway, so the bar does not poll; it asks again the moment the exam ends.
  const provPagarRef = useRef(provPagar);
  const provSlutRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    const forut = provPagarRef.current;
    provPagarRef.current = provPagar;
    if (forut && !provPagar) provSlutRef.current?.();
  }, [provPagar]);

  useEffect(() => {
    let cancelled = false;
    let timer: number | null = null;
    let abort: AbortController | null = null;
    let currentDelay = AKTIV_LEKTION_IDLE_MS;
    let nastaTickAt = 0;
    // The last answer (undefined before the first): a page change re-applies the rule to it.
    let senaste: AktivLektion | null | undefined;
    // The student's repetition done-flags, read with the answer only when they may have changed (first poll,
    // page change, visible, "repetition handed in", an unknown repetition): ?rep=0 otherwise, and the bar keeps
    // these. Until 2026-10-31 every poll read RepetitionGjord while the lesson had repetitions.
    const gjorda = new Map<string, boolean>();
    let lasRep = true;
    // LONG WAIT during a lesson (Erik 2026-11-02, aktiv-lektion route): the bar asks with the version it has and the
    // server answers the moment the class's signal moves — the invitation within about a second of the teacher's
    // start instead of 0–15 s (30 s on an activity's page). skapaLiveKlient decides when to ask (visible tab, not
    // switched off after trouble), bounds each request with its own timeout (a hung request used to stall the whole
    // chain) and gives the pause before the next one; without it the bar keeps its interval as before.
    const live = skapaLiveKlient(false);
    let version: string | null = null;
    let provOppnat: string | null | undefined;
    const intervallFor = (aktiv: AktivLektion | null) =>
      aktivLektionIntervall({
        lektion: !!aktiv,
        // A Begreppsjakt the student has not joined waits for them like a game does.
        spelVantar: (!!aktiv?.spel && !aktiv.spel.deltar) || (!!aktiv?.begreppsjakt && !aktiv.begreppsjakt.med) || (!!aktiv?.mattedetektiv && !aktiv.mattedetektiv.med) || (!!aktiv?.problemlosning && !aktiv.problemlosning.med),
        genomgangId: aktiv?.genomgang?.id ?? null,
        sokvag: pathnameRef.current,
        egnaAktivitetssidor: egnaAktivitetssidor(aktiv),
      });
    // Always replace the one pending timer: a tick whose fetch was overtaken (visibility,
    // page change) still schedules in its finally, and without the clear the
    // two chains would both live on until a full reload (pre-deploy review 2026-10-21).
    const schemalagg = (ms: number) => {
      if (timer !== null) window.clearTimeout(timer);
      nastaTickAt = Date.now() + ms;
      timer = window.setTimeout(tick, ms);
    };

    async function tick() {
      // Hidden tab: no fetch (every student polls this on every page; it was the most
      // frequent call 2026-10-21, prestanda-rattningslatens.md §10b P13). Becoming
      // visible polls at once (onSynlig below).
      if (document.hidden || provPagarRef.current) {
        if (!cancelled) schemalagg(currentDelay);
        return;
      }
      const egen = new AbortController();
      abort = egen;
      let nastaPaus: number | null = null;
      // Wait from the second poll on (the first fetches the version): the server waits on the class's signal with or
      // without a running lesson — a lesson the teacher starts is a change too — and answers at once when it cannot.
      const vanta = senaste !== undefined && live.borja(true);
      const tid = live.medTimeout(egen.signal);
      const t0 = Date.now();
      try {
        const medRep = lasRep;
        const q = new URLSearchParams();
        if (!medRep) q.set("rep", "0");
        if (version) q.set("v", version);
        if (vanta) q.set("w", "1");
        const qs = q.toString();
        const res = await fetch(`/api/student/aktiv-lektion${qs ? `?${qs}` : ""}`, {
          cache: "no-store",
          signal: tid.signal,
        });
        if (cancelled) return;
        if (!res.ok) {
          if (vanta) live.fel();
          return;
        }
        const data = (await res.json()) as {
          aktiv?: AktivLektion | null;
          v?: string;
          oforandrad?: boolean;
          vantade?: boolean;
          provOppnat?: string | null;
          nasta?: number;
        };
        // The bar shows itself (and every popup) only inside the lesson's minutes by the client's clock, which
        // otherwise moves every 30 s: an answer that starts the lesson must not wait for it.
        setNow(new Date());
        // An answer without a version (no student, a teacher, a running prov: the route answers at once) can never
        // be waited on: it counts as nothing new, so it never earns the short pause — no tight loop.
        const versionerad = !!data.oforandrad || typeof data.v === "string";
        if (vanta) live.svar({ vantade: !!data.vantade, nytt: versionerad && !data.oforandrad, ms: Date.now() - t0 });
        if (!data.oforandrad) {
          const aktiv = data.aktiv ?? null;
          if (aktiv?.repetitioner) {
            if (medRep) gjorda.clear();
            aktiv.repetitioner = aktiv.repetitioner.map((r) => {
              const k = repNyckel(r);
              if (typeof r.gjord === "boolean") {
                gjorda.set(k, r.gjord);
                return r;
              }
              const kand = gjorda.get(k);
              if (kand === undefined) lasRep = true; // a repetition we hold no flag for: read them next time
              return { ...r, gjord: kand ?? false };
            });
          }
          if (medRep) lasRep = false;
          version = data.v ?? null;
          setLektion(aktiv);
          senaste = aktiv;
          // A prov the teacher just opened: the start page builds its "Prov väntar" card on the server — refresh it.
          const prov = data.provOppnat ?? null;
          if (provOppnat !== undefined && prov !== null && prov !== provOppnat && pathnameRef.current === "/") {
            routerRef.current.refresh();
          }
          provOppnat = prov;
        }
        currentDelay = intervallFor(senaste ?? null);
        if (vanta) nastaPaus = live.paus();
        // The first answer only fetched the version: start waiting at once (a page load used to sit out a whole
        // interval before its first wait — measured 12 s locally 2026-11-02) — unless the live client is switched off.
        else if (versionerad && live.borja(true)) nastaPaus = LIVE_PAUS_MIN_MS;
        else if (typeof data.nasta === "number") nastaPaus = Math.min(currentDelay, data.nasta + 500);
      } catch {
        // Abort at a page change or a visible tab is a kick, not a failure; the client's own timeout is.
        if (vanta && tid.tidUte()) live.fel();
      } finally {
        tid.stang();
        // A tick overtaken by a kick (page change, visible tab, "ask now") leaves the scheduling to the kick: its
        // late finally used to replace the kick's short timer with the full interval.
        if (!cancelled && !egen.signal.aborted) schemalagg(nastaPaus ?? currentDelay);
      }
    }

    // A page change postpones the next poll (at least 1.5 s, so it does not compete with the
    // navigation's render). It brings it forward only when the new page's rule is shorter — leaving the running
    // genomgång's page (30 s) for one where its popup must come within 5 s; it used to schedule an extra poll
    // 1.5 s after every navigation.
    rescheduleRef.current = () => {
      lasRep = true;
      if (abort) abort.abort();
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
      if (senaste !== undefined) currentDelay = intervallFor(senaste);
      schemalagg(Math.max(AKTIV_LEKTION_NAV_PAUS_MS, Math.min(nastaTickAt - Date.now(), currentDelay)));
    };

    function onSynlig() {
      if (document.hidden || cancelled) return;
      lasRep = true;
      if (timer !== null) window.clearTimeout(timer);
      if (abort) abort.abort();
      void tick();
    }
    provSlutRef.current = onSynlig;
    document.addEventListener("visibilitychange", onSynlig);
    // The genomgång page announces its end (it polls the genomgång itself, and the bar waits 30 s there):
    // ask at once, so a game the teacher starts next is offered without that wait.
    window.addEventListener(AKTIV_LEKTION_NU, onSynlig);

    tick();
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onSynlig);
      window.removeEventListener(AKTIV_LEKTION_NU, onSynlig);
      if (abort) abort.abort();
      if (timer !== null) window.clearTimeout(timer);
      rescheduleRef.current = null;
      provSlutRef.current = null;
    };
  }, []);

  // Pathname-byte → pausa polling 1500 ms så nav-rendering inte konkurrerar
  // med poll-fetchen om Vercel-funktionsslot + Turso-anslutning.
  // Only an actual change counts: the mount run (twice under StrictMode) used
  // to abort the initial fetch and hold the bar back 1.5 s on every page load.
  const senastePathnameRef = useRef(pathname);
  useEffect(() => {
    if (senastePathnameRef.current === pathname) return;
    senastePathnameRef.current = pathname;
    rescheduleRef.current?.();
  }, [pathname]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const startMin = useMemo(
    () => (lektion ? timeToMinutes(lektion.start) : null),
    [lektion]
  );
  const slutMin = useMemo(
    () => (lektion ? timeToMinutes(lektion.slut) : null),
    [lektion]
  );

  if (!lektion || startMin === null || slutMin === null || slutMin <= startMin)
    return null;

  const totalLangd = slutMin - startMin;
  const nuMin = now.getHours() * 60 + now.getMinutes();
  if (nuMin < startMin || nuMin > slutMin) return null;
  const nuPct = ((nuMin - startMin) / totalLangd) * 100;

  const spel = lektion.spel;
  const visaSpelPopup =
    !!spel &&
    !spel.deltar &&
    spel.id !== avvisatSpelId &&
    !harTackatNejTillSpel(spel.id) &&
    !pathname.startsWith("/spel");

  const genomgang = lektion.genomgang ?? null;
  const paGenomgangssidan = pathname.startsWith("/genomgang/");
  const genomgangsNyckel = genomgang ? genomgangNyckel(genomgang) : null;
  // Popupen gäller starten av bildspelet och varje nyöppnad fråga, inte lösningsfasen.
  const visaGenomgangPopup =
    !!genomgang &&
    genomgangsNyckel !== null &&
    genomgang.fas !== "losning" &&
    !paGenomgangssidan &&
    !visaSpelPopup &&
    !avvisadeGenomgangar.includes(genomgangsNyckel) &&
    !harTackatNejTillGenomgang(genomgangsNyckel);

  const reflektion = lektion.reflektion ?? null;
  const paReflektionssidan = pathname.startsWith("/reflektion/");
  const visaReflektionPopup =
    !!reflektion &&
    !reflektion.besvarad &&
    !paReflektionssidan &&
    !visaSpelPopup &&
    !visaGenomgangPopup &&
    reflektion.id !== avvisadReflektion &&
    !harTackatNejTillReflektion(reflektion.id);

  const begreppsjakt = lektion.begreppsjakt ?? null;
  const paJaktsidan = pathname.startsWith("/begreppsjakt/");
  const visaJaktPopup =
    !!begreppsjakt &&
    !begreppsjakt.med &&
    !paJaktsidan &&
    !visaSpelPopup &&
    !visaGenomgangPopup &&
    !visaReflektionPopup &&
    begreppsjakt.id !== avvisadJakt &&
    !harTackatNejTillJakt(begreppsjakt.id);

  const mattedetektiv = lektion.mattedetektiv ?? null;
  const paDetektivsidan = pathname.startsWith("/mattedetektiv/");
  const visaDetektivPopup =
    !!mattedetektiv &&
    !mattedetektiv.med &&
    !paDetektivsidan &&
    !visaSpelPopup &&
    !visaGenomgangPopup &&
    !visaReflektionPopup &&
    !visaJaktPopup &&
    mattedetektiv.id !== avvisadDetektiv &&
    !harTackatNejTillDetektiv(mattedetektiv.id);

  const problemlosning = lektion.problemlosning ?? null;
  const oppnaFragor = problemlosning?.slag === "oppna";
  const paProblemlosningssidan = pathname.startsWith("/problemlosning/");
  const visaProblemlosningPopup =
    !!problemlosning &&
    !problemlosning.med &&
    !paProblemlosningssidan &&
    !visaSpelPopup &&
    !visaGenomgangPopup &&
    !visaReflektionPopup &&
    !visaJaktPopup &&
    !visaDetektivPopup &&
    problemlosning.id !== avvisadProblemlosning &&
    !harTackatNejTillProblemlosning(problemlosning.id);

  // Tooltip på tidslinjen — lektion-tid följt av varje rast på egen rad.
  const tooltipText = [
    `Lektion ${lektion.start}–${lektion.slut}`,
    ...lektion.raster.map((r) => `Rast ${r.start}`),
  ].join("\n");

  return (
    <div
      className="flex items-center gap-2 md:max-w-[33rem] md:min-w-[18rem] md:flex-1"
      style={{ marginTop: "0.5rem" }}
    >
      {/* Dok-ikon för extra material — synlig när läraren lagt till länkar */}
      {(lektion.extraMaterial?.length ?? 0) > 0 && (
        <button
          type="button"
          onClick={() => setMaterialModalOpen(true)}
          aria-label="Extra material"
          title="Visa lärarens extra-material för lektionen"
          className="p-1.5 rounded-md transition-colors flex-shrink-0 inline-flex items-center justify-center"
          style={{
            background: "transparent",
            border: "none",
            color: "var(--color-nav-text-muted)",
            cursor: "pointer",
            lineHeight: 1,
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--color-nav-text)"
            strokeWidth={1.75}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="9" y1="13" x2="15" y2="13" />
            <line x1="9" y1="17" x2="15" y2="17" />
          </svg>
        </button>
      )}

      {/* Repetition-ikon — synlig när lektionen har rep-DA tillagda. Visar
          en cykel-pil + badge med antal ogjorda. Klick öppnar RepListaModal. */}
      {(lektion.repetitioner?.length ?? 0) > 0 && (() => {
        const reps = lektion.repetitioner ?? [];
        const ogjordaCount = reps.filter((r) => !r.gjord).length;
        return (
          <button
            type="button"
            onClick={() => setRepModalOpen(true)}
            aria-label="Repetitions-delavsnitt"
            title={`Repetitioner i lektionen: ${ogjordaCount} kvar att göra`}
            className="relative p-1.5 rounded-md transition-colors flex-shrink-0 inline-flex items-center justify-center"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--color-nav-text)"
              strokeWidth={1.75}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            {ogjordaCount > 0 && (
              <span
                aria-hidden="true"
                className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center rounded-full text-[10px] font-mono leading-none"
                style={{
                  width: "14px",
                  height: "14px",
                  background: "var(--color-figur-mossa)",
                  color: "white",
                }}
              >
                {ogjordaCount > 9 ? "9+" : ogjordaCount}
              </span>
            )}
          </button>
        );
      })()}

      {/* Titel + progress-bar — bara på desktop (md+) */}
      <div className="hidden md:flex items-center flex-1" style={{ gap: "0.67cm" }}>
        <span
          className="text-xs font-medium flex-shrink-0"
          style={{ color: "var(--color-nav-text)" }}
          title={lektion.titel || "Aktiv lektion"}
        >
          Lektion:
        </span>
        <div className="relative flex-1">
          {/* Klockslag ovanför nu-markören — kompakt placerad strax över baren */}
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              left: `${nuPct}%`,
              top: "-0.9rem",
              transform: "translateX(-50%)",
              fontSize: "0.72rem",
              lineHeight: 1,
              color: "var(--color-nav-text-muted)",
              fontVariantNumeric: "tabular-nums",
              pointerEvents: "none",
              whiteSpace: "nowrap",
            }}
          >
            {minutesToHHMM(nuMin)}
          </span>
          <div
            className="relative h-2 rounded-full overflow-hidden"
            style={{ background: "var(--color-figur-salvia)" }}
            data-tip={tooltipText}
          >
            {lektion.raster.map((rast) => {
              const rastStartMin = timeToMinutes(rast.start);
              if (rastStartMin === null) return null;
              const rastEndMin = rastStartMin + rast.langd;
              const left = ((rastStartMin - startMin) / totalLangd) * 100;
              const width = ((rastEndMin - rastStartMin) / totalLangd) * 100;
              if (left + width < 0 || left > 100) return null;
              return (
                <div
                  key={rast.id}
                  className="absolute top-0 bottom-0"
                  style={{
                    left: `${Math.max(0, left)}%`,
                    width: `${Math.min(100, width)}%`,
                    background: "var(--color-figur-sand)",
                  }}
                />
              );
            })}
            <div
              className="absolute top-0 bottom-0 w-1 rounded-full"
              style={{
                left: `${nuPct}%`,
                transform: "translateX(-50%)",
                background: "var(--color-figur-rost)",
              }}
            />
          </div>
        </div>
      </div>

      {/* Spel-ikon — synlig längst till höger så länge klassens Matte-opardy
          pågår (lobby eller pågår). Diskret pulserande prick tills eleven
          gått med. */}
      {spel && (
        <a
          href={`/spel/${spel.id}`}
          aria-label={spelIkonText(spel)}
          title={spelIkonText(spel)}
          className="relative p-1.5 rounded-md transition-colors flex-shrink-0 inline-flex items-center justify-center"
          style={{
            background: "transparent",
            border: "none",
            color: "var(--color-nav-text)",
            lineHeight: 1,
          }}
        >
          <AktivitetLampa lyser />
          {!spel.deltar && (
            <span
              aria-hidden="true"
              className="absolute -top-0.5 -right-0.5 rounded-full"
              style={{
                width: "8px",
                height: "8px",
                background: "var(--color-accent)",
                boxShadow: "0 0 0 1.5px var(--color-nav-bg)",
                animation: "recordBlink 1.4s ease-in-out infinite",
              }}
            />
          )}
        </a>
      )}

      {spel &&
        visaSpelPopup &&
        typeof document !== "undefined" &&
        createPortal(
          <Modal
            open
            title={
              spel.rostning
                ? "Läraren har startat ett spel."
                : `Läraren har startat ${SPEL_NAMN[spel.spelTyp]}.`
            }
            message={spel.rostning ? "Vill du vara med och rösta på vilket spel ni ska spela?" : "Vill du vara med?"}
            confirmLabel="Gå med"
            cancelLabel="Inte nu"
            confirmTitle={spelIkonText(spel)}
            cancelTitle="Stäng utan att gå med. Spelikonen uppe till höger finns kvar om du ändrar dig."
            // Full load, not router.push: a tab open since before a deploy
            // keeps old code that may not know a newly added game
            // (2026-09-30: Matte-hjulet crashed in such tabs).
            onConfirm={() => window.location.assign(`/spel/${spel.id}`)}
            onCancel={() => {
              markeraNejTillSpel(spel.id);
              setAvvisatSpelId(spel.id);
            }}
          />,
          document.body,
        )}

      {/* Genomgångs-ikon — synlig så länge klassens interaktiva genomgång pågår.
          En pulserande prick visar att en fråga är öppen och eleven inte redan är där. */}
      {genomgang && (
        <a
          href={`/genomgang/${genomgang.id}`}
          aria-label={genomgangIkonText(genomgang)}
          title={genomgangIkonText(genomgang)}
          className="relative p-1.5 rounded-md transition-colors flex-shrink-0 inline-flex items-center justify-center"
          style={{
            background: "transparent",
            border: "none",
            color: "var(--color-nav-text)",
            lineHeight: 1,
          }}
        >
          <AktivitetLampa lyser />
          {genomgang.fas === "ovning" && !paGenomgangssidan && (
            <span
              aria-hidden="true"
              className="absolute -top-0.5 -right-0.5 rounded-full"
              style={{
                width: "8px",
                height: "8px",
                background: "var(--color-accent)",
                boxShadow: "0 0 0 1.5px var(--color-nav-bg)",
                animation: "recordBlink 1.4s ease-in-out infinite",
              }}
            />
          )}
        </a>
      )}

      {genomgang &&
        genomgangsNyckel !== null &&
        visaGenomgangPopup &&
        typeof document !== "undefined" &&
        createPortal(
          <Modal
            open
            title={
              genomgang.fas === "ovning"
                ? "Läraren har öppnat en fråga."
                : "Läraren har startat en genomgång."
            }
            message={
              genomgang.fas === "ovning"
                ? "Svara på din skärm. Du väljer själv vilket steg du vill svara på."
                : "Öppna den för att följa med och svara på frågorna."
            }
            confirmLabel={genomgang.fas === "ovning" ? "Svara" : "Öppna"}
            cancelLabel="Inte nu"
            confirmTitle={genomgangIkonText(genomgang)}
            cancelTitle="Stäng utan att öppna. Ikonen uppe till höger finns kvar om du ändrar dig."
            // Full load, like the game: a tab open since before a deploy keeps old code.
            onConfirm={() => window.location.assign(`/genomgang/${genomgang.id}`)}
            onCancel={() => {
              markeraNejTillGenomgang(genomgangsNyckel);
              setAvvisadeGenomgangar((nu) => [...nu, genomgangsNyckel]);
            }}
          />,
          document.body,
        )}

      {/* Lektionsreflektionens ikon — synlig medan den är öppen; pricken tills eleven svarat. */}
      {reflektion && (
        <a
          href={`/reflektion/${reflektion.id}`}
          aria-label={reflektion.besvarad ? "Ändra ditt svar i lektionsreflektionen" : "Svara på lektionsreflektionen"}
          title={reflektion.besvarad ? "Ändra ditt svar i lektionsreflektionen" : "Svara på lektionsreflektionen"}
          className="relative p-1.5 rounded-md transition-colors flex-shrink-0 inline-flex items-center justify-center"
          style={{ background: "transparent", border: "none", color: "var(--color-nav-text)", lineHeight: 1 }}
        >
          <AktivitetLampa lyser />
          {!reflektion.besvarad && !paReflektionssidan && (
            <span
              aria-hidden="true"
              className="absolute -top-0.5 -right-0.5 rounded-full"
              style={{
                width: "8px",
                height: "8px",
                background: "var(--color-accent)",
                boxShadow: "0 0 0 1.5px var(--color-nav-bg)",
                animation: "recordBlink 1.4s ease-in-out infinite",
              }}
            />
          )}
        </a>
      )}

      {reflektion &&
        visaReflektionPopup &&
        typeof document !== "undefined" &&
        createPortal(
          <Modal
            open
            title="Läraren har startat en lektionsreflektion."
            message="Fundera en stund på dagens lektion och svara på din skärm. Det tar ett par minuter."
            confirmLabel="Svara"
            cancelLabel="Inte nu"
            confirmTitle="Öppna lektionsreflektionen och svara"
            cancelTitle="Stäng utan att svara. Ikonen uppe till höger finns kvar om du ändrar dig."
            onConfirm={() => window.location.assign(`/reflektion/${reflektion.id}`)}
            onCancel={() => {
              markeraNejTillReflektion(reflektion.id);
              setAvvisadReflektion(reflektion.id);
            }}
          />,
          document.body,
        )}

      {/* Begreppsjaktens ikon — synlig medan jakten pågår; pricken tills eleven gått med. */}
      {begreppsjakt && (
        <a
          href={`/begreppsjakt/${begreppsjakt.id}`}
          aria-label={begreppsjakt.med ? "Gå till begreppsjakten" : "Gå med i begreppsjakten"}
          title={begreppsjakt.med ? "Gå till begreppsjakten" : "Gå med i begreppsjakten"}
          className="relative p-1.5 rounded-md transition-colors flex-shrink-0 inline-flex items-center justify-center"
          style={{ background: "transparent", border: "none", color: "var(--color-nav-text)", lineHeight: 1 }}
        >
          <AktivitetLampa lyser />
          {!begreppsjakt.med && !paJaktsidan && (
            <span
              aria-hidden="true"
              className="absolute -top-0.5 -right-0.5 rounded-full"
              style={{
                width: "8px",
                height: "8px",
                background: "var(--color-accent)",
                boxShadow: "0 0 0 1.5px var(--color-nav-bg)",
                animation: "recordBlink 1.4s ease-in-out infinite",
              }}
            />
          )}
        </a>
      )}

      {begreppsjakt &&
        visaJaktPopup &&
        typeof document !== "undefined" &&
        createPortal(
          <Modal
            open
            title="Läraren har startat Begreppsjakten!"
            message="Du får ett begrepp eller en definition. Gå runt i klassrummet och hitta den som hör ihop med dig."
            confirmLabel="Gå med"
            cancelLabel="Inte nu"
            confirmTitle="Gå med i begreppsjakten"
            cancelTitle="Stäng utan att gå med. Ikonen uppe till höger finns kvar om du ändrar dig."
            onConfirm={() => window.location.assign(`/begreppsjakt/${begreppsjakt.id}`)}
            onCancel={() => {
              markeraNejTillJakt(begreppsjakt.id);
              setAvvisadJakt(begreppsjakt.id);
            }}
          />,
          document.body,
        )}

      {/* Matte-detektivens ikon — synlig medan aktiviteten pågår; pricken tills eleven gått med. */}
      {mattedetektiv && (
        <a
          href={`/mattedetektiv/${mattedetektiv.id}`}
          aria-label={mattedetektiv.med ? "Gå till Matte-detektiven" : "Gå med i Matte-detektiven"}
          title={mattedetektiv.med ? "Gå till Matte-detektiven" : "Gå med i Matte-detektiven"}
          className="relative p-1.5 rounded-md transition-colors flex-shrink-0 inline-flex items-center justify-center"
          style={{ background: "transparent", border: "none", color: "var(--color-nav-text)", lineHeight: 1 }}
        >
          <AktivitetLampa lyser />
          {!mattedetektiv.med && !paDetektivsidan && (
            <span
              aria-hidden="true"
              className="absolute -top-0.5 -right-0.5 rounded-full"
              style={{
                width: "8px",
                height: "8px",
                background: "var(--color-accent)",
                boxShadow: "0 0 0 1.5px var(--color-nav-bg)",
                animation: "recordBlink 1.4s ease-in-out infinite",
              }}
            />
          )}
        </a>
      )}

      {mattedetektiv &&
        visaDetektivPopup &&
        typeof document !== "undefined" &&
        createPortal(
          <Modal
            open
            title="Läraren har startat Matte-detektiven!"
            message="Du löser tre detektivuppdrag, ett i taget, och väljer själv steg. Gå med så ser du ditt första uppdrag."
            confirmLabel="Gå med"
            cancelLabel="Inte nu"
            confirmTitle="Gå med i Matte-detektiven"
            cancelTitle="Stäng utan att gå med. Ikonen uppe till höger finns kvar om du ändrar dig."
            onConfirm={() => window.location.assign(`/mattedetektiv/${mattedetektiv.id}`)}
            onCancel={() => {
              markeraNejTillDetektiv(mattedetektiv.id);
              setAvvisadDetektiv(mattedetektiv.id);
            }}
          />,
          document.body,
        )}

      {/* Problemlösningens (eller Öppna frågors) ikon — synlig medan aktiviteten pågår; pricken tills eleven gått med. */}
      {problemlosning && (
        <a
          href={`/problemlosning/${problemlosning.id}`}
          aria-label={`${problemlosning.med ? "Gå till" : "Gå med i"} ${oppnaFragor ? "Öppna frågor" : "problemlösningen"}`}
          title={`${problemlosning.med ? "Gå till" : "Gå med i"} ${oppnaFragor ? "Öppna frågor" : "problemlösningen"}`}
          className="relative p-1.5 rounded-md transition-colors flex-shrink-0 inline-flex items-center justify-center"
          style={{ background: "transparent", border: "none", color: "var(--color-nav-text)", lineHeight: 1 }}
        >
          <AktivitetLampa lyser />
          {!problemlosning.med && !paProblemlosningssidan && (
            <span
              aria-hidden="true"
              className="absolute -top-0.5 -right-0.5 rounded-full"
              style={{
                width: "8px",
                height: "8px",
                background: "var(--color-accent)",
                boxShadow: "0 0 0 1.5px var(--color-nav-bg)",
                animation: "recordBlink 1.4s ease-in-out infinite",
              }}
            />
          )}
        </a>
      )}

      {problemlosning &&
        visaProblemlosningPopup &&
        typeof document !== "undefined" &&
        createPortal(
          <Modal
            open
            title={oppnaFragor ? "Läraren har startat Öppna frågor" : "Läraren har startat problemlösning"}
            message={
              oppnaFragor
                ? "Varje omgång får du en öppen fråga på Steg I, II och III. Det finns inget rätt svar: gör egna antaganden och fota din lösning."
                : "Varje omgång får du ett problem på Steg I, II och III. Lös med de fyra stegen och fota din lösning."
            }
            confirmLabel="Gå med"
            cancelLabel="Inte nu"
            confirmTitle={oppnaFragor ? "Gå med i Öppna frågor" : "Gå med i problemlösningen"}
            cancelTitle="Stäng utan att gå med. Ikonen uppe till höger finns kvar om du ändrar dig."
            onConfirm={() => window.location.assign(`/problemlosning/${problemlosning.id}`)}
            onCancel={() => {
              markeraNejTillProblemlosning(problemlosning.id);
              setAvvisadProblemlosning(problemlosning.id);
            }}
          />,
          document.body,
        )}

      {materialModalOpen && (
        <ExtraMaterialModal
          materials={lektion.extraMaterial ?? []}
          onClose={() => setMaterialModalOpen(false)}
        />
      )}

      {repModalOpen && (
        <RepListaModal
          titel="Repetitioner i lektionen"
          items={lektion.repetitioner ?? []}
          onClose={() => setRepModalOpen(false)}
        />
      )}
    </div>
  );
}

function ExtraMaterialModal({
  materials,
  onClose,
}: {
  materials: ExtraMaterial[];
  onClose: () => void;
}) {
  const [activeIdx, setActiveIdx] = useState(0);
  const active = materials[activeIdx];

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    document.body.dataset.modalOpen = "true";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      delete document.body.dataset.modalOpen;
    };
  }, [onClose]);

  if (!active) return null;
  // Portal to body so the modal leaves the Nav (z-30) stacking context and its
  // z-50 backdrop dims the header + impersonation banner like the other overlays.
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Extra material"
    >
      <div
        className="relative rounded-lg w-[96vw] h-[94vh] max-w-7xl flex flex-col shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ background: "var(--color-bg)" }}
      >
        {/* Top-bar: tabs + open-in-new-tab + stäng — samma design som FormelbladButton */}
        <div
          className="flex items-end justify-between px-3 pt-2 overflow-x-auto"
          style={{
            background: "var(--color-nav-bg)",
            borderBottom: "1px solid color-mix(in oklab, var(--color-nav-bg) 70%, black)",
          }}
        >
          <div className="flex items-end gap-1 text-xs">
            {materials.map((m, idx) => {
              const active = idx === activeIdx;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setActiveIdx(idx)}
                  title={m.titel || "Utan titel"}
                  className="px-2.5 py-1 whitespace-nowrap transition-colors"
                  style={{
                    border: "1px solid color-mix(in oklab, var(--color-nav-bg) 65%, black)",
                    borderBottom: active
                      ? "1px solid var(--color-bg)"
                      : "1px solid color-mix(in oklab, var(--color-nav-bg) 65%, black)",
                    borderTopLeftRadius: "0.25rem",
                    borderTopRightRadius: "0.25rem",
                    color: active ? "var(--color-text)" : "var(--color-text-muted)",
                    background: active ? "var(--color-bg)" : "var(--color-sidebar-bg)",
                    marginBottom: "-1px",
                  }}
                >
                  {m.titel || `Material ${idx + 1}`}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-1 mb-1">
            <a
              href={active.pdfUrl ?? active.url}
              target="_blank"
              rel="noopener noreferrer"
              title={
                active.pdfUrl
                  ? "Öppna PDF:en i en ny flik"
                  : "Öppna i ny flik (fungerar för alla länkar — vissa sajter blockerar inbäddning)"
              }
              className="text-xs px-2.5 py-1 rounded-md transition-colors hover:bg-black/10"
              style={{ color: "var(--color-text)" }}
            >
              Öppna i ny flik ↗
            </a>
            <button
              type="button"
              onClick={onClose}
              aria-label="Stäng"
              title="Stäng (Esc)"
              className="p-1.5 rounded-md transition-colors hover:bg-black/10"
              style={{ color: "var(--color-text)" }}
            >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          </div>
        </div>
        {/* PDF:er serveras från vår egen same-origin-route (/api/lektion/.../pdf)
            och MÅSTE renderas utan sandbox — Chrome blockerar sin inbyggda PDF-
            visare i sandboxade iframes (tom ruta + "blockerad av Chrome", men
            funkar i ny flik). Externa länkar (active.url) sandboxas fortfarande
            eftersom de är otrusted tredjepartsinnehåll. */}
        <iframe
          src={active.pdfUrl ?? active.url}
          title={active.titel || active.pdfFilnamn || "Extra material"}
          className="flex-1 w-full bg-white"
          {...(active.pdfUrl
            ? {}
            : {
                sandbox:
                  "allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox",
              })}
        />
      </div>
    </div>,
    document.body,
  );
}
